import { Image } from 'expo-image';
import * as Linking from 'expo-linking';
import {
  CalendarDaysIcon,
  ExternalLinkIcon,
  FlagIcon,
  ImageIcon,
  ImagesIcon,
  InfoIcon,
  MapPinIcon,
  RouteIcon,
  VideoIcon,
  type LucideIcon,
} from 'lucide-react-native';
import * as React from 'react';
import { ActionSheetIOS, Pressable, View } from 'react-native';

import { EventDetailSheet } from '@/components/events/event-detail-sheet';
import { ReportContentSheet } from '@/components/moderation/report-content-sheet';
import { Button } from '@sixam/ui/button';
import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';
import {
  EMPTY_EVENT_ASSETS,
  eventMediaPublicUrl,
  fetchEventAssets,
  type EventAssets,
  type EventLink,
  type EventMedia,
} from '@/lib/event-assets';
import { formatEventWhen, isEventFull, spotsLeft, type Event } from '@/lib/events';
import { androidMapsUrl, appleMapsUrl, mapsUrl, wazeMapsUrl } from '@/lib/event-location';
import { RoutePreview } from '@/components/routes/route-preview';
import { fetchRouteVersion, type RouteLibraryItem } from '@/lib/routes';
import { useReleaseFeatureFlag } from '@/lib/use-release-feature-flag';

export type EventDetailSheetKind = 'about' | 'media' | 'planning' | 'route';

function openEventLocation(
  event: Pick<Event, 'place_id' | 'place_lat' | 'place_lng' | 'place_name'>
) {
  if (process.env.EXPO_OS === 'ios') {
    const mapOptions = [
      { label: 'Apple Maps', url: appleMapsUrl(event) },
      { label: 'Google Maps', url: mapsUrl(event) },
      { label: 'Waze', url: wazeMapsUrl(event) },
    ];
    const cancelButtonIndex = mapOptions.length;

    ActionSheetIOS.showActionSheetWithOptions(
      {
        cancelButtonIndex,
        options: [...mapOptions.map(({ label }) => label), 'Cancel'],
        title: event.place_name,
      },
      (selectedIndex) => {
        const selectedOption = mapOptions[selectedIndex];
        if (selectedOption) {
          void Linking.openURL(selectedOption.url);
        }
      }
    );
    return;
  }

  const locationUrl = process.env.EXPO_OS === 'android' ? androidMapsUrl(event) : mapsUrl(event);
  void Linking.openURL(locationUrl);
}

type EventDetailsProps = Readonly<{
  assets?: EventAssets | null;
  event: Event;
  footer?: React.ReactNode;
  initialSheet?: EventDetailSheetKind | null;
  managementAction?: React.ReactNode;
  planningTools?: React.ReactNode;
  primaryAction?: React.ReactNode;
  route?: RouteLibraryItem | null;
  showAttendance?: boolean;
  timeZone?: string;
}>;

// Event details composes independently releasable route, media, location, and attendance surfaces.
export function EventDetails({
  assets,
  event,
  footer,
  initialSheet = null,
  managementAction,
  planningTools,
  primaryAction,
  route,
  showAttendance = true,
  timeZone,
}: EventDetailsProps) {
  const eventMediaEnabled = useReleaseFeatureFlag('eventMedia') === true;
  const routePreviewsEnabled = useReleaseFeatureFlag('routePreviews') === true;
  const left = spotsLeft(event);
  const full = isEventFull(event);
  const [activeSheet, setActiveSheet] = React.useState<EventDetailSheetKind | null>(initialSheet);
  const [loadedAssets, setLoadedAssets] = React.useState<EventAssets | null>(assets ?? null);
  const [isLoadingAssets, setIsLoadingAssets] = React.useState(assets === undefined);
  const [assetError, setAssetError] = React.useState<string | null>(null);
  const [loadedRoute, setLoadedRoute] = React.useState<RouteLibraryItem | null>(route ?? null);
  const [isLoadingRoute, setIsLoadingRoute] = React.useState(
    routePreviewsEnabled && route === undefined && Boolean(event.route_version_id)
  );
  const [routeError, setRouteError] = React.useState<string | null>(null);
  const [isReportPresented, setIsReportPresented] = React.useState(false);
  const [isBlockedAuthorHidden, setIsBlockedAuthorHidden] = React.useState(false);
  // Uploads are the one kind of content no text filter can catch, so they need
  // their own way to be flagged.
  const [reportedMedia, setReportedMedia] = React.useState<EventMedia | null>(null);

  function hideBlockedAuthor() {
    setIsBlockedAuthorHidden(true);
    setIsReportPresented(false);
    setReportedMedia(null);
  }

  React.useEffect(() => {
    let active = true;

    async function loadAssets() {
      if (assets !== undefined) {
        setLoadedAssets(assets);
        setIsLoadingAssets(false);
        return;
      }

      setIsLoadingAssets(true);
      try {
        const nextAssets = await fetchEventAssets(event.id);
        if (active) {
          setLoadedAssets(nextAssets);
          setAssetError(null);
        }
      } catch (error) {
        if (active) {
          setAssetError(error instanceof Error ? error.message : 'Could not load event media.');
        }
      } finally {
        if (active) {
          setIsLoadingAssets(false);
        }
      }
    }

    void loadAssets();

    return () => {
      active = false;
    };
  }, [assets, event.id]);

  React.useEffect(() => {
    let active = true;
    async function loadRoute() {
      if (!routePreviewsEnabled) {
        setLoadedRoute(null);
        setIsLoadingRoute(false);
        return;
      }
      if (route !== undefined) {
        setLoadedRoute(route);
        setIsLoadingRoute(false);
        return;
      }
      if (!event.route_version_id) {
        setLoadedRoute(null);
        setIsLoadingRoute(false);
        return;
      }
      setIsLoadingRoute(true);
      try {
        const nextRoute = await fetchRouteVersion(event.route_version_id);
        if (active) {
          setLoadedRoute(nextRoute);
          setRouteError(null);
        }
      } catch (error) {
        if (active) {
          setRouteError(error instanceof Error ? error.message : 'Could not load this route.');
        }
      } finally {
        if (active) setIsLoadingRoute(false);
      }
    }
    void loadRoute();
    return () => {
      active = false;
    };
  }, [event.route_version_id, route, routePreviewsEnabled]);

  const visibleAssets = loadedAssets ?? EMPTY_EVENT_ASSETS;
  const hasMediaDetails =
    isLoadingAssets ||
    Boolean(assetError) ||
    (eventMediaEnabled && visibleAssets.media.length > 0) ||
    visibleAssets.links.length > 0;
  let attendanceCopy = `${event.attendee_count} going · no limit`;
  if (event.capacity != null) {
    const availabilityCopy = full ? 'full' : `${left} left`;
    attendanceCopy = `${event.attendee_count}/${event.capacity} going · ${availabilityCopy}`;
  }

  if (isBlockedAuthorHidden) {
    return (
      <View className="gap-2 rounded-xl border border-border bg-card p-5">
        <Text variant="h2">Content hidden</Text>
        <Text variant="muted">
          This member and their event content were removed from your view immediately.
        </Text>
      </View>
    );
  }

  return (
    <View className="gap-4">
      <View className="gap-1">
        <Text variant="h2">{event.title}</Text>
        <Text variant="mono">{formatEventWhen(event.starts_at, event.ends_at, timeZone)}</Text>
        {event.status === 'cancelled' ? (
          <Text className="font-medium text-destructive">This event has been cancelled.</Text>
        ) : null}
      </View>

      {primaryAction}

      {routePreviewsEnabled && (event.route_version_id || route !== undefined) ? (
        <Pressable
          accessibilityHint="Opens full route details"
          accessibilityLabel={loadedRoute ? `Open route ${loadedRoute.route.name}` : 'Open route'}
          accessibilityRole="button"
          className="active:opacity-80"
          disabled={!loadedRoute}
          onPress={() => setActiveSheet('route')}
        >
          <RoutePreview
            distanceM={loadedRoute?.version.distance_m}
            elevationGainM={loadedRoute?.version.elevation_gain_m}
            error={routeError}
            geometry={loadedRoute?.version.geometry}
            isLoading={isLoadingRoute}
            name={loadedRoute?.route.name}
          />
        </Pressable>
      ) : null}

      <Pressable
        accessibilityHint="Lets you choose a map app"
        accessibilityLabel={`Open ${event.place_name} in Maps`}
        accessibilityRole="button"
        className="overflow-hidden rounded-xl border border-border bg-card active:opacity-80"
        onPress={() => openEventLocation(event)}
      >
        <View className="min-h-14 flex-row items-center gap-3 px-3 py-2">
          <Icon as={MapPinIcon} className="size-5 text-primary" />
          <View className="min-w-0 flex-1">
            <Text variant="label" numberOfLines={1}>
              {event.place_name}
            </Text>
            <Text variant="muted" numberOfLines={1}>
              {event.place_address}
            </Text>
          </View>
          <View className="items-end">
            <Text variant="label" className="text-primary">
              OPEN MAPS
            </Text>
            <Icon as={ExternalLinkIcon} className="size-3.5 text-primary" />
          </View>
        </View>
      </Pressable>

      {showAttendance ? (
        <View className="flex-row items-baseline justify-between gap-4 border-b border-border pb-3">
          <Text variant="label" className="text-muted-foreground">
            ATTENDANCE
          </Text>
          <Text variant="mono" className="text-right">
            {attendanceCopy}
          </Text>
        </View>
      ) : null}

      <EventDetailActions
        hasAbout={Boolean(event.description)}
        hasMedia={hasMediaDetails}
        hasPlanning={Boolean(planningTools)}
        hasRoute={routePreviewsEnabled && Boolean(loadedRoute)}
        managementAction={managementAction}
        onSelect={setActiveSheet}
      />

      {footer}

      {/*
        Every member can report an event. It sits at the end rather than beside
        the actions, because reporting is rare and should not compete with the
        things people came here to do.
      */}
      <Pressable
        accessibilityRole="button"
        className="flex-row items-center justify-center gap-2 py-3 active:opacity-70"
        onPress={() => setIsReportPresented(true)}
      >
        <Icon as={FlagIcon} className="size-4 text-muted-foreground" />
        <Text variant="muted">Report this event</Text>
      </Pressable>

      <ReportContentSheet
        contentLabel={reportedMedia?.alt_text ?? 'this photo or video'}
        isPresented={reportedMedia !== null}
        onBlocked={hideBlockedAuthor}
        onDismiss={() => setReportedMedia(null)}
        subjectId={reportedMedia?.id ?? ''}
        subjectType="event_media"
        subjectUserId={event.created_by}
      />

      <ReportContentSheet
        contentLabel={event.title}
        isPresented={isReportPresented}
        onBlocked={hideBlockedAuthor}
        onDismiss={() => setIsReportPresented(false)}
        subjectId={event.id}
        subjectType="event"
        subjectUserId={event.created_by}
      />

      <EventDetailSheet
        isPresented={activeSheet === 'planning'}
        onDismiss={() => setActiveSheet(null)}
        title="Add to calendar"
      >
        {planningTools}
      </EventDetailSheet>

      <EventDetailSheet
        isPresented={activeSheet === 'about'}
        onDismiss={() => setActiveSheet(null)}
        title="About this event"
      >
        <Text selectable>{event.description}</Text>
      </EventDetailSheet>

      <EventDetailSheet
        isPresented={routePreviewsEnabled && activeSheet === 'route'}
        onDismiss={() => setActiveSheet(null)}
        title="Run route"
      >
        {loadedRoute ? (
          <View className="gap-4">
            <RoutePreview
              distanceM={loadedRoute.version.distance_m}
              elevationGainM={loadedRoute.version.elevation_gain_m}
              geometry={loadedRoute.version.geometry}
              name={loadedRoute.route.name}
            />
            {loadedRoute.route.description ? (
              <Text selectable>{loadedRoute.route.description}</Text>
            ) : null}
            <Text variant="muted">
              Course version {loadedRoute.version.version}. Meet-up location is shown separately.
            </Text>
          </View>
        ) : null}
      </EventDetailSheet>

      {/*
        There is no separate location sheet. The bar above already names the
        place, gives the address, and opens Maps — a sheet behind a "Location"
        button repeated all three and made the page read as if the event had two
        locations.
      */}

      <EventDetailSheet
        isPresented={hasMediaDetails && activeSheet === 'media'}
        onDismiss={() => setActiveSheet(null)}
        title="Media & links"
      >
        <EventAssetsSection
          assets={visibleAssets}
          onReportMedia={(media) => setReportedMedia(media)}
          error={assetError}
          isLoading={isLoadingAssets}
          showMedia={eventMediaEnabled}
        />
      </EventDetailSheet>
    </View>
  );
}

function EventDetailActions({
  hasAbout,
  hasMedia,
  hasPlanning,
  hasRoute,
  managementAction,
  onSelect,
}: Readonly<{
  hasAbout: boolean;
  hasMedia: boolean;
  hasPlanning: boolean;
  hasRoute: boolean;
  managementAction?: React.ReactNode;
  onSelect: (sheet: EventDetailSheetKind) => void;
}>) {
  return (
    <View className="gap-1">
      {hasAbout || managementAction ? (
        <View className="flex-row items-center gap-1">
          {hasAbout ? (
            <EventDetailAction
              fill
              icon={InfoIcon}
              label="About"
              onPress={() => onSelect('about')}
              outlined
            />
          ) : null}
          {managementAction ? <View className="min-w-0 flex-1">{managementAction}</View> : null}
        </View>
      ) : null}

      {hasPlanning || hasRoute || hasMedia ? (
        <View className="flex-row flex-wrap items-center gap-1">
          {hasPlanning ? (
            <EventDetailAction
              icon={CalendarDaysIcon}
              label="Calendar"
              onPress={() => onSelect('planning')}
            />
          ) : null}
          {hasRoute ? (
            <EventDetailAction icon={RouteIcon} label="Route" onPress={() => onSelect('route')} />
          ) : null}
          {hasMedia ? (
            <EventDetailAction
              icon={ImagesIcon}
              label="Media & links"
              onPress={() => onSelect('media')}
            />
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

function EventDetailAction({
  fill = false,
  icon,
  label,
  onPress,
  outlined = false,
}: Readonly<{
  fill?: boolean;
  icon: LucideIcon;
  label: string;
  onPress: () => void;
  outlined?: boolean;
}>) {
  return (
    <View className={fill ? 'min-w-0 flex-1' : undefined}>
      <Button
        className={`${fill ? 'h-11 w-full gap-1.5 px-2' : 'h-11 gap-1.5 px-3'} ${
          outlined ? 'border-border bg-background' : ''
        }`}
        onPress={onPress}
        size="sm"
        variant="ghost"
      >
        <Icon as={icon} className="size-4 text-foreground" />
        <Text
          adjustsFontSizeToFit
          className="min-w-0 text-foreground"
          minimumFontScale={0.82}
          numberOfLines={1}
          style={{ letterSpacing: 1.2 }}
        >
          {label}
        </Text>
      </Button>
    </View>
  );
}

function EventAssetsSection({
  assets,
  error,
  isLoading,
  onReportMedia,
  showMedia,
}: Readonly<{
  assets: EventAssets;
  error: string | null;
  isLoading: boolean;
  onReportMedia?: (media: EventMedia) => void;
  showMedia: boolean;
}>) {
  if (isLoading) {
    return <Text variant="muted">Loading event media...</Text>;
  }

  if (error) {
    return (
      <Text className="text-sm text-destructive" selectable>
        {error}
      </Text>
    );
  }

  if ((!showMedia || assets.media.length === 0) && assets.links.length === 0) {
    return <Text variant="muted">No media or links have been added.</Text>;
  }

  return (
    <View className="gap-6">
      {showMedia && assets.media.length > 0 ? (
        <View className="gap-3">
          <Text variant="label" className="text-muted-foreground">
            MEDIA
          </Text>
          {assets.media.map((media) => (
            <EventMediaItem key={media.id} media={media} onReport={onReportMedia} />
          ))}
        </View>
      ) : null}
      {assets.links.length > 0 ? (
        <View className="gap-1">
          <Text variant="label" className="text-muted-foreground">
            LINKS
          </Text>
          {assets.links.map((link) => (
            <EventLinkRow key={link.id} link={link} />
          ))}
        </View>
      ) : null}
    </View>
  );
}

function EventMediaItem({
  media,
  onReport,
}: Readonly<{ media: EventMedia; onReport?: (media: EventMedia) => void }>) {
  const publicUrl = eventMediaPublicUrl(media.storage_path);
  const label = media.alt_text ?? (media.media_type === 'video' ? 'Event video' : 'Event photo');

  if (media.media_type === 'video') {
    return (
      <View className="flex-row items-center gap-3 border-b border-border py-3">
        <Icon as={VideoIcon} className="size-5 text-foreground" />
        <View className="min-w-0 flex-1">
          <Text variant="label" numberOfLines={1}>
            {label}
          </Text>
          <Text variant="muted">Video</Text>
        </View>
        <Button onPress={() => void Linking.openURL(publicUrl)} size="sm" variant="secondary">
          <Text>Open</Text>
        </Button>
        {onReport ? (
          <Button
            accessibilityLabel="Report this video"
            onPress={() => onReport(media)}
            size="icon"
            variant="ghost"
          >
            <Icon as={FlagIcon} className="size-4 text-muted-foreground" />
          </Button>
        ) : null}
      </View>
    );
  }

  return (
    <Pressable
      accessibilityRole="link"
      className="gap-2 active:opacity-80"
      onPress={() => void Linking.openURL(publicUrl)}
    >
      <Image className="h-52 w-full rounded-xl" contentFit="cover" source={{ uri: publicUrl }} />
      <View className="flex-row items-center gap-2">
        <Icon as={ImageIcon} className="size-4 text-muted-foreground" />
        <Text variant="muted" numberOfLines={1}>
          {label}
        </Text>
      </View>
    </Pressable>
  );
}

function EventLinkRow({ link }: Readonly<{ link: EventLink }>) {
  return (
    <Pressable
      accessibilityRole="link"
      className="min-h-11 flex-row items-center gap-3 border-b border-border py-3 active:opacity-70"
      onPress={() => void Linking.openURL(link.url)}
    >
      <View className="min-w-0 flex-1">
        <Text variant="label" numberOfLines={1}>
          {link.label}
        </Text>
        <Text variant="muted" numberOfLines={1}>
          {link.url}
        </Text>
      </View>
      <Icon as={ExternalLinkIcon} className="size-4 text-muted-foreground" />
    </Pressable>
  );
}
